import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/ui/use-toast';
import { Calendar, Users, Trophy, Share2, Settings, QrCode, FileText, ArrowDown } from 'lucide-react';
import { competitionsService } from '../services/competitions';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import SponsorsStrip from '../components/location/SponsorsStrip';
import CompetitionParticipantsPanel from '../components/competition/CompetitionParticipantsPanel';
import CompetitionRoundsList from '../components/competition/CompetitionRoundsList';
import { availableRoundViews } from '../components/competition/round-views';
import CategoryStandingsLinks from '../components/competition/CategoryStandingsLinks';
import CompetitionResults from '../components/competition/CompetitionResults';
import TeamEntriesSection from '../components/competition/TeamEntriesSection';
import { useTeamEntries } from '../components/competition/useTeamEntries';
import StageEntryAction from '../components/trophy/StageEntryAction';
import StageEntryDialogs from '../components/trophy/StageEntryDialogs';
import EnteredAthletes from '../components/guardian/EnteredAthletes';
import {
  hasWardToEnter,
  useGuardianWards,
  viewerEnteredIn,
  wardsEnteredIn,
  withTurnToChoose,
  withoutCategory,
} from '../components/guardian/useGuardianWards';
import { useResumeStageEntry, useStageEntry } from '../components/trophy/useStageEntry';
import { useViewerPlacement, useWardsHaveSeat } from '../components/trophy/useViewerPlacement';
import { roundEntryOffers, type RoundEntryOffer } from '../components/trophy/entry-options';
import LocationHeader from '../components/location/LocationHeader';
import GymStaffActions from '../components/gym/GymStaffActions';
import { competitionBadge } from '../utils/lifecycle-status';
import { canDownloadInstructions } from '../components/competition-instructions/instructions-access';
import type { Competition } from '../types/competition';
import type { StageParticipation } from '../types/trophy';
import type { GalleryImage } from '../types/location-images';

// Shared styling for icon buttons overlaid on the cover image, as on gym-profile.
const COVER_OVERLAY_BUTTON = 'h-9 w-9 bg-black/40 border-white/20 text-white hover:bg-black/60 hover:text-white';

export default function CompetitionProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
  // Bumped whenever this account's entries change here, so the rounds' seats
  // and where the viewer would be entered are read again.
  const [entriesVersion, setEntriesVersion] = useState(0);

  // "Sign me up": the same ask-then-enter flow as the circuit's page, and the
  // row redrawn from the server's answer once they are in.
  const entered = useCallback((standing: StageParticipation) => {
    setCompetition((current) =>
      current ? { ...current, participation: standing, is_participant: true } : current
    );
    setEntriesVersion((version) => version + 1);
  }, []);
  // Read once for the page: each round card draws the children of this account
  // whose class it runs, and the picker behind the button uses the same list.
  const { wards, reload: reloadWards } = useGuardianWards();
  const entry = useStageEntry(entered, { wards, onWardsChanged: reloadWards });

  // Everybody on this start list who belongs to this account: the viewer, and
  // the children whose accounts they hold. A round's card shows the ones whose
  // class it runs; the ones the desk has not sorted yet are shown above the
  // rounds, since no round is theirs to sit in.
  const myAthletes = useMemo(
    () => [
      ...viewerEnteredIn(competition?.participation ?? null, user ?? null),
      ...wardsEnteredIn(wards, slug ?? ''),
    ],
    [competition?.participation, user, wards, slug]
  );
  const unsorted = useMemo(() => withoutCategory(myAthletes), [myAthletes]);
  // Sorted into a class that is split over turns, with none booked: above the
  // rounds as well, where the turn is chosen.
  const turnToChoose = useMemo(() => withTurnToChoose(myAthletes), [myAthletes]);

  // Back from signing up to enter this stage: the dialog opens on it.
  const resumableStages = useMemo(
    () => (competition?.participation ? [competition.participation] : []),
    [competition?.participation]
  );
  useResumeStageEntry(entry.requestEnter, resumableStages);

  // Somebody whose category the server can already work out — gender and date
  // of birth known — enters from the round(s) that category climbs, one entry
  // per stage: each turn it may book, or the one round it climbs. The top
  // button stays only for a parent with children still to enter, whose picker
  // it opens.
  const placement = useViewerPlacement(slug, competition?.participation ?? null, !!user, entriesVersion);
  const entryOffers = useMemo(() => roundEntryOffers(placement), [placement]);
  // A child still to enter keeps the top button only while a round has a seat
  // for one of them: a parent whose children's turns are all full has nobody
  // left to enter from this side.
  const wardIdsToEnter = useMemo(
    () => wards.filter((ward) => hasWardToEnter([ward], slug ?? '')).map((ward) => ward.id),
    [wards, slug]
  );
  const wardsHaveSeat = useWardsHaveSeat(competition?.participation?.open ? slug : null, wardIdsToEnter, entriesVersion);
  const othersToEnter = wardIdsToEnter.length > 0 && wardsHaveSeat;
  const entersFromRounds = entryOffers.length > 0 && !othersToEnter;
  const enterOnRound = useCallback(
    (offer: RoundEntryOffer) => {
      const stage = competition?.participation;
      if (!stage || !user) return;
      // Named as the viewer, so the flow asks nobody "who for?" and the dialog
      // speaks to them; the turn the card was is picked already.
      entry.requestEnter(
        stage,
        { user_id: String(user.id), full_name: user.full_name || user.email || '' },
        { roundId: offer.bookRoundId }
      );
    },
    [competition?.participation, entry, user]
  );

  // The viewer's own entry comes off `competition`, so withdrawing it has to
  // re-read the page as well as the children.
  const reloadEntries = useCallback(() => {
    reloadWards();
    setEntriesVersion((version) => version + 1);
    if (slug) competitionsService.getCompetitionBySlug(slug).then(setCompetition).catch(() => undefined);
  }, [reloadWards, slug]);

  // A squad's team manager: their squads, read once for the squads' cards and
  // the rounds' alike.
  const managesTeams = (competition?.managed_teams?.length ?? 0) > 0;
  const squad = useTeamEntries(slug, managesTeams);

  const currentUserStaff = user?.staff?.find(staff => staff.gym_slug === slug) || null;
  const isStaffAdmin = currentUserStaff?.staff_roles.includes('admin') && currentUserStaff?.status === 'confirmed';
  const isStaffMember = currentUserStaff?.status === 'confirmed' &&
    (currentUserStaff?.staff_roles.includes('admin') || currentUserStaff?.staff_roles.includes('editor'));
  const isJudge = currentUserStaff?.staff_roles.includes('judge') && currentUserStaff?.status === 'confirmed';
  // Whoever hands the entry instructions out: the event's admins and its desk,
  // by the same rule the instructions page applies.
  const sharesInstructions = !!slug && canDownloadInstructions(user, slug);

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;
    const fetchCompetition = async () => {
      setLoading(true);
      let competitionData: Competition | null = null;
      let competitionError: unknown = null;

      try {
        competitionData = await competitionsService.getCompetitionBySlug(slug);
      } catch (err) {
        competitionError = err;
      }

      if (isMounted) {
        setCompetition(competitionData);
        setLoading(false);

        if (competitionError) {
          console.error('Failed to fetch competition profile:', competitionError);
          toast({
            title: t('competitionProfile.failedToLoad'),
            description: competitionError instanceof Error ? competitionError.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
        }
      }
    };

    fetchCompetition();
    return () => { isMounted = false; };
  }, [slug, t, toast]);

  const handleGoToSettings = () => {
    navigate(`/competitions/${slug}/settings`);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: competition?.name || t('competitionProfile.share'),
          text: t('competitionProfile.shareText', { name: competition?.name }),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({ title: t('competitionProfile.linkCopied'), description: t('competitionProfile.linkCopiedDescription'), duration: 3000 });
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
        toast({ title: t('errors.shareFailed'), description: t('errors.shareFailedDescription'), variant: 'destructive' });
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // A stage's name means little on its own — "Tappa 1" — so the circuit it
  // belongs to leads the trail, and is the way back up to it.
  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: competition
        ? [
            ...(competition.trophy
              ? [{
                  label: competition.trophy.name,
                  onClick: () => navigate(`/trophies/${competition.trophy!.slug}`),
                }]
              : []),
            { label: competition.name },
          ]
        : [],
      loading,
    }),
    [competition?.name, competition?.trophy?.slug, competition?.trophy?.name, loading]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardContent className="p-0">
            <Skeleton className="w-full aspect-[16/6] rounded-t-lg" />
            <div className="pt-14 px-6 pb-6 space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-32" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card><CardContent className="pt-6 text-center"><h1 className="text-2xl font-bold text-muted-foreground">{t('competitionProfile.notFound')}</h1></CardContent></Card>
      </div>
    );
  }

  // The phase, the desk's hour and — when the entries are held up rather than
  // shut (see StageEntryAction) — that, so the badge and the button below it
  // never say different things.
  // Over (or put away): the results lead the page, every class at once.
  const concluded = competition.status === 'closed' || competition.status === 'archived';

  const statusBadge = competitionBadge(
    competition.status,
    competition.entries_open,
    !!competition.participation?.entry_block
  );

  return (
    <>
      {/* Full-bleed hero, the same treatment as a gym's profile: the cover spans
          the viewport, the logo sits on it, and the actions are overlaid on the
          image rather than stacked under it. What sits below is the
          competition's own — the rounds — not a gym's list of walls. */}
      <Card className="-mt-3 -mx-4 sm:-mx-6 rounded-none border-0 bg-transparent shadow-none">
        <CardContent className="p-0">
          {/* Cover + Logo + Name + Bio */}
          <LocationHeader
            locationId={competition.id}
            name={competition.name}
            bio={competition.bio}
            coverImageUrl={competition.cover_image_url}
            logoUrl={competition.logo_url}
            logoSmallUrl={competition.logo_small_url}
            galleryImages={competition.gallery_images ?? []}
            fallbackIcon={<Trophy className="h-8 w-8 text-muted-foreground" />}
            isStaffMember={!!isStaffMember}
            isStaffAdmin={!!isStaffAdmin}
            hideName
            logoOverlay
            fullBleed
            coverSlideshow
            coverTopRight={
              <>
                <Button variant="outline" size="icon" onClick={handleShare} title={t('competitionProfile.share')} className={COVER_OVERLAY_BUTTON}>
                  <Share2 className="h-4 w-4" />
                </Button>

                {/* The printable poster for the venue — staff only. */}
                {isStaffMember && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/competitions/${slug}/qr-poster`)}
                    title={t('qrPoster.button')}
                    className={COVER_OVERLAY_BUTTON}
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                )}

                {sharesInstructions && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/competitions/${slug}/instructions.pdf`)}
                    title={t('competitionInstructions.page.button')}
                    aria-label={t('competitionInstructions.page.button')}
                    className={COVER_OVERLAY_BUTTON}
                    data-testid="instructions-button"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                )}

                {isStaffAdmin && (
                  <Button variant="outline" size="icon" onClick={handleGoToSettings} title={t('competitionProfile.settings')} className={COVER_OVERLAY_BUTTON}>
                    <Settings className="h-4 w-4" />
                  </Button>
                )}
              </>
            }
            coverBottomLeft={<Badge variant={statusBadge.variant}>{t(statusBadge.labelKey)}</Badge>}
            onNameUpdated={newName => setCompetition(c => c ? { ...c, name: newName } : c)}
            onBioUpdated={newBio => setCompetition(c => c ? { ...c, bio: newBio } : c)}
            onCoverUpdated={url => setCompetition(c => c ? { ...c, cover_image_url: url } : c)}
            onLogoUpdated={(url, smallUrl) => setCompetition(c => c ? { ...c, logo_url: url, logo_small_url: smallUrl } : c)}
            onGalleryUpdated={(imgs: GalleryImage[]) => setCompetition(c => c ? { ...c, gallery_images: imgs } : c)}
          />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-6 pt-3 space-y-4">
            {/* Who backs it: the circuit's sponsors, then this one's own. Where
                the link back to the circuit used to be — the breadcrumb gives
                that way up. */}
            <SponsorsStrip sponsors={competition.sponsors ?? []} />

            {/* Address + Date range */}
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm">{competition.full_address}</p>
              {(competition.start_date || competition.end_date) && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {formatDate(competition.start_date)}
                    {competition.start_date && competition.end_date && ' — '}
                    {formatDate(competition.end_date)}
                  </span>
                </div>
              )}
            </div>

            {isStaffAdmin && (
              <div className="flex gap-2 items-center flex-wrap">
                <Button variant="outline" onClick={() => setShowParticipantsPanel(true)} size="sm" className="gap-2">
                  <Users className="h-4 w-4" />
                  {t('competitionProfile.manageParticipants')}
                </Button>
              </div>
            )}

            {/* An invitation to this competition's staff — a judge's, say — is
                answered here, as a gym's is on the gym's page. Confirmed admins
                have the settings button above and need no card. */}
            {currentUserStaff && !isStaffAdmin && (
              <GymStaffActions gymId={competition.id} gymSlug={slug} currentUserStaff={currentUserStaff} kind="competition" />
            )}

            {/* Where the viewer stands, and the one thing they can do about
                it: entered (with their class), or the button to enter — the
                same control as on the circuit's page. Beside it, the judge
                badge for whoever scores here. */}
            <div className="flex flex-wrap items-center gap-2">
              {entersFromRounds ? (
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="enter-from-rounds">
                  <ArrowDown className="size-4 shrink-0" aria-hidden />
                  {t(entryOffers.every((offer) => offer.full) ? 'stageEntry.noSeatsLeft' : 'stageEntry.enterFromRounds', {
                    category: placement?.category?.name ?? '',
                  })}
                </p>
              ) : (
                competition.participation && (
                  <StageEntryAction
                    stage={competition.participation}
                    signedIn={!!user}
                    busy={entry.preparing}
                    othersToEnter={othersToEnter}
                    onRequestEnter={entry.requestEnter}
                  />
                )
              )}
              {(isJudge || competition.is_judge) && (
                <Badge variant="outline" className="text-blue-600 border-blue-600">
                  <Trophy className="h-3 w-3 mr-1" />
                  {t('competitionProfile.youAreJudge')}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* A squad's team manager puts their athletes on the start list from
            here, one card per squad — the job that matters on this page before
            the weekend, so it leads. Only for somebody named on a squad here
            (`managed_teams`), which keeps the request off everybody else's
            visit, and only while entries are open: once they close there is
            nobody left to enter, and the squad's athletes are on the cards of
            the rounds they climb. */}
        {slug && managesTeams && competition.entries_open && (
          <TeamEntriesSection
            competitionSlug={slug}
            competitionName={competition.name}
            teamNames={competition.managed_teams ?? []}
            squad={squad}
          />
        )}

        {/* Entered, and in no round's card yet because the desk has not put a
            class on them. Above the rounds rather than nowhere: this is the
            only place the entry can be seen — and taken back. */}
        {unsorted.length > 0 && (
          <Card data-testid="unsorted-entries">
            <CardContent className="space-y-3 p-4">
              <div>
                <h2 className="text-sm font-semibold">{t('competitionProfile.unsortedTitle')}</h2>
                <p className="text-xs text-muted-foreground">{t('competitionProfile.unsortedHint')}</p>
              </div>
              <EnteredAthletes
                entered={unsorted}
                competitionSlug={slug ?? ''}
                onWithdrawn={reloadEntries}
              />
            </CardContent>
          </Card>
        )}

        {/* In a class split over two or more turns, and no turn booked: the
            boards hold them on every turn until they choose, and this is where
            they do (while entries are open — afterwards the desk does). */}
        {turnToChoose.length > 0 && (
          <Card data-testid="turn-to-choose-entries">
            <CardContent className="space-y-3 p-4">
              <div>
                <h2 className="text-sm font-semibold">{t('competitionProfile.turnToChooseTitle')}</h2>
                <p className="text-xs text-muted-foreground">{t('competitionProfile.turnToChooseHint')}</p>
              </div>
              <EnteredAthletes
                entered={turnToChoose}
                competitionSlug={slug ?? ''}
                onWithdrawn={reloadEntries}
              />
            </CardContent>
          </Card>
        )}

        {slug && concluded && <CompetitionResults competitionSlug={slug} />}

        {/* The rounds, for everybody — a visitor deciding whether to come
            wants to know when each one starts as much as an entrant does.
            Gone once the stage is over: what is left to read is the results. */}
        {competition && slug && !concluded && (
          <CompetitionRoundsList
            competitionId={competition.id}
            competitionSlug={slug}
            isAdmin={isStaffAdmin || false}
            isJudge={isJudge || competition.is_judge || false}
            // Who still has marks to write once the clock has stopped, and so
            // reaches a round whatever state it is in: staff or a judge.
            canScore={!!isStaffMember || !!isJudge || !!competition.is_judge}
            // A squad's team manager (`managed_teams` is non-empty only for
            // one): reaches a round once it has started, and before then its
            // card unfolds the squad's athletes in it.
            squad={managesTeams ? squad : undefined}
            athletes={myAthletes}
            onWardsChanged={reloadEntries}
            entryOffers={entersFromRounds ? entryOffers : []}
            onEnterRound={enterOnRound}
            entryBusy={entry.preparing || entry.entering}
            refreshKey={entriesVersion}
            roundViews={(round) =>
              availableRoundViews({ staff: user?.staff, competition, categoryIds: round.category_ids })
            }
          />
        )}

        {/* A class split over turns is one contest: its table across them,
            once every turn is closed. Draws nothing until then, and nothing
            once the stage is over — the results above have every class. */}
        {slug && !concluded && <CategoryStandingsLinks competitionSlug={slug} />}
      </div>

      {showParticipantsPanel && competition && (
        <CompetitionParticipantsPanel competitionId={competition.id} onClose={() => setShowParticipantsPanel(false)} />
      )}

      <StageEntryDialogs entry={entry} trophyName={competition.trophy?.name ?? competition.name} />
    </>
  );
}
